import { AgentTargetViewModel } from '../ViewModels';
import {
    ActionStatusHelper,
    DomainTableConfig,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';

import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useAgentTarget } from '@/OneLiners';
import { AgentTargetHeader } from '../agent-target-config';
import { useAgentTargetEnricher } from './useAgentTrgetEnricher';
import { AgentTargetModel } from '../AgentTargetModel';

/**
 * useAgentTargetTableConfig hook
 *
 * Domain-specific layout composition hook for AgentTarget entities.
 * Acts as the composition layer between:
 * - AgentTarget domain data access,
 * - enrichment logic and derived field resolution,
 * - permission state aggregation,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Combine domain and enrichment readiness states into a single contract.
 * - Provide dynamic header configuration for the AgentTarget table.
 * - Define strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * This hook is declarative by design.
 * It specifies domain dependencies and transformations,
 * while lifecycle mechanics remain centralized in infrastructure.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<AgentTargetViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<AgentTargetViewModel>
 */
export function useAgentTargetTableConfig(props: DomainHookProps): DomainTableConfig<AgentTargetViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.AgentTarget;

    /**
     * AgentTarget domain entry point.
     */
    const agentTargetHook = useAgentTarget();

    /**
     * AgentTarget enrichment layer.
     *
     * Responsible for resolving user relationships
     * and computing derived AgentTarget fields.
     */
    const { userMap, getStrictEnrichedAgentTarget, enricherResponse, } = useAgentTargetEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle will only initialize once
     * this contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            agentTargetHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status,
            //accessRight.status
        ),
        error: agentTargetHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: agentTargetHook,

        /**
         * Header configuration for Property table.
         */
        headers: AgentTargetHeader.build(userMap),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            agentTarget: AgentTargetModel, isDisplaying: boolean) =>
            new AgentTargetViewModel({
                agentTarget: getStrictEnrichedAgentTarget(agentTarget),
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}